import { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send, Loader2, Phone } from "lucide-react";
import { API } from "@/api";
import { SITE } from "@/siteConfig";
import { useQuote } from "./QuoteModal";
import { useContent } from "@/content/ContentContext";

const WELCOME =
  "Hello, and welcome to Modify. How can I help you today?";

const CHIPS = [
  "I need a grab rail installed",
  "Hand rail for steps or stairs",
  "A ramp for a step or doorway",
  "Something else",
];

function getSessionId() {
  let id = localStorage.getItem("modify_chat_session");
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem("modify_chat_session", id);
  }
  return id;
}

export default function ChatWidget() {
  const { openQuote } = useQuote();
  const { get } = useContent();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([{ role: "assistant", content: WELCOME }]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const sessionRef = useRef(getSessionId());
  const listRef = useRef(null);
  const inputRef = useRef(null);
  const phoneHref = get("site_phone_href", SITE.phoneHref);
  const phoneDisplay = get("site_phone_display", SITE.phoneDisplay);

  useEffect(() => {
    if (sessionStorage.getItem("lara_greeted")) return undefined;
    const t = setTimeout(() => {
      setOpen(true);
      sessionStorage.setItem("lara_greeted", "1");
    }, 35000);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages, sending]);

  const send = async (text) => {
    const message = (text ?? input).trim();
    if (!message || sending) return;
    setInput("");
    setSending(true);
    setMessages((m) => [...m, { role: "user", content: message }, { role: "assistant", content: "" }]);
    try {
      const resp = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionRef.current, message }),
      });
      if (!resp.ok || !resp.body) throw new Error("Chat request failed");
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop() || "";
        for (const part of parts) {
          if (!part.startsWith("data: ")) continue;
          const payload = part.slice(6);
          if (payload === "[DONE]") continue;
          try {
            const parsed = JSON.parse(payload);
            if (parsed.delta) {
              setMessages((m) => {
                const next = [...m];
                next[next.length - 1] = { role: "assistant", content: next[next.length - 1].content + parsed.delta };
                return next;
              });
            }
            if (parsed.error) throw new Error(parsed.error);
          } catch (err) {
            if (err.message !== "Unexpected end of JSON input") throw err;
          }
        }
      }
    } catch {
      setMessages((m) => {
        const next = [...m];
        next[next.length - 1] = {
          role: "assistant",
          content: `Sorry, I'm having trouble right now. Please call us on ${phoneDisplay} or use the Get a Quote form and we'll look after you.`,
        };
        return next;
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {!open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          data-testid="chat-widget-button"
          aria-label="Chat with Lara, the Modify assistant"
          className="fixed bottom-20 right-4 z-[60] flex h-14 w-14 items-center justify-center rounded-full bg-ember text-charcoal shadow-2xl transition-transform duration-300 hover:scale-105 md:bottom-6 md:right-6"
        >
          <MessageCircle className="h-6 w-6" aria-hidden="true" />
          <span className="absolute -right-0.5 -top-0.5 flex h-3.5 w-3.5" aria-hidden="true">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-green-400 opacity-75" />
            <span className="relative inline-flex h-3.5 w-3.5 rounded-full border-2 border-ivory bg-green-500" />
          </span>
        </button>
      )}

      {open && (
        <div
          data-testid="chat-panel"
          role="dialog"
          aria-label="Chat with Lara, the Modify assistant"
          className="fixed inset-x-3 bottom-[4.5rem] z-[60] flex h-[70vh] max-h-[620px] flex-col overflow-hidden rounded-3xl border border-line bg-white shadow-2xl sm:inset-x-auto sm:right-6 sm:bottom-24 sm:w-[400px] md:bottom-6"
        >
          <div className="flex items-center gap-3 bg-charcoal px-5 py-4 text-ivory">
            <img
              src="/images/lara.png"
              alt="Lara, Modify assistant"
              className="h-10 w-10 rounded-full object-cover ring-2 ring-sage/50"
            />
            <div className="flex-1">
              <p className="font-display text-base font-bold leading-tight">Lara</p>
              <p className="flex items-center gap-1.5 text-xs text-ivory/70">
                <span className="animate-blink h-2 w-2 rounded-full bg-green-400" aria-hidden="true" />
                Modify Assistant — online 24/7
              </p>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              data-testid="chat-close-button"
              aria-label="Close chat"
              className="flex h-10 w-10 items-center justify-center rounded-full text-ivory/80 transition-colors duration-200 hover:bg-white/10"
            >
              <X className="h-5 w-5" aria-hidden="true" />
            </button>
          </div>

          <div ref={listRef} data-testid="chat-messages" role="log" aria-live="polite" className="flex-1 space-y-3 overflow-y-auto bg-ivory px-4 py-4">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  data-testid={`chat-message-${i}`}
                  className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                    m.role === "user"
                      ? "rounded-br-md bg-charcoal text-white"
                      : "rounded-bl-md border border-line bg-white text-ink"
                  }`}
                >
                  {m.content || (sending && i === messages.length - 1 ? "…" : "")}
                </div>
              </div>
            ))}
            {messages.length === 1 && !sending && (
              <div className="flex flex-wrap gap-2 pt-1">
                {CHIPS.map((chip) => (
                  <button
                    key={chip}
                    type="button"
                    onClick={() => send(chip)}
                    data-testid={`chat-chip-${chip.toLowerCase().replace(/\s+/g, "-")}`}
                    className="min-h-[40px] rounded-full border border-line bg-white px-4 text-xs font-bold text-ink/70 transition-colors duration-200 hover:border-sage hover:text-charcoal"
                  >
                    {chip}
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => {
                    setOpen(false);
                    openQuote();
                  }}
                  data-testid="chat-chip-get-a-quote"
                  className="min-h-[40px] rounded-full bg-charcoal px-4 text-xs font-bold text-white transition-colors duration-200 hover:bg-ink"
                >
                  Get a quote
                </button>
              </div>
            )}
          </div>

          <div className="border-t border-line bg-white px-3 py-3">
            <div className="flex items-center gap-2">
              <a
                href={phoneHref}
                data-testid="chat-call-button"
                aria-label={`Call Modify on ${phoneDisplay}`}
                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-line text-charcoal transition-colors duration-200 hover:bg-sand"
              >
                <Phone className="h-4 w-4" aria-hidden="true" />
              </a>
              <label htmlFor="chat-input" className="sr-only">
                Message Lara
              </label>
              <input
                id="chat-input"
                ref={inputRef}
                data-testid="chat-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") send();
                }}
                placeholder="Ask Lara anything..."
                maxLength={1000}
                className="min-h-[44px] flex-1 rounded-full border border-line bg-ivory px-4 text-sm text-ink placeholder:text-ink/40 focus:border-sage focus:outline-none focus:ring-2 focus:ring-sage/30"
              />
              <button
                type="button"
                onClick={() => send()}
                disabled={sending || !input.trim()}
                data-testid="chat-send-button"
                aria-label="Send message"
                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-charcoal text-white transition-colors duration-200 hover:bg-ink disabled:opacity-50"
              >
                {sending ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <Send className="h-4 w-4" aria-hidden="true" />}
              </button>
            </div>
            <p className="mt-2 text-center text-[11px] text-ink/45">
              We care about making everyday living safer.
            </p>
          </div>
        </div>
      )}
    </>
  );
}
